/**
 * <ActivityCopyColumn> — la copie de l'élève en colonne, à côté de l'énoncé, sur un grand écran
 * (organism).
 *
 * La barre de copie (`<ActivityCopyBar>`) quand la place le permet : l'énoncé à gauche, la copie à
 * droite, dans le volet de `<TwoPaneTemplate listSide="right">`. Les mêmes props, les mêmes
 * gestes ; ce que la colonne a de plus, c'est la place :
 *  - « Mon travail » en intitulé (le nom de la boîte de l'ancien code), et la consigne de l'étape
 *    de rendu toujours lisible dessous ;
 *  - les pages en grandes vignettes, deux par ligne, toutes visibles (la colonne défile) ;
 *  - « Ajouter une photo » en bouton en pointillés au bout des pages ;
 *  - « J'ai fini » en pied, sur toute la largeur de la colonne, qui ne défile pas.
 *
 * Un geste à la fois, comme la barre (Wajih, 16/10) : sans rien de rendu, « Prendre ma copie en
 * photo » (ou « Enregistrer mon travail ») est LE bouton de la colonne, plein, sous la consigne ;
 * le pied n'existe pas encore. « J'ai fini » arrive avec la première page.
 *
 * `floating` est la place de la tuile de Sam : elle flotte au bas de la colonne, à droite (au-dessus
 * de « J'ai fini » quand il est là), sans jamais couvrir le bas de ce qui défile (la colonne lui
 * garde la hauteur de la tuile). En live, la bulle tandem est posée plus haut (`<FloatingSlot>`) : la
 * colonne lui déclare « J'ai fini » (`useFloatingBase`), et lui garde sa place au pied de ce qui défile,
 * hors du défilement — ses boutons (« Prendre ma copie en photo », « Ajouter une photo ») ne passent
 * jamais dessous.
 */
import React from "react"
import { ScrollView, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { formatMinutesSeconds } from "../../../ports/primary/ui/formatters/dateFormatter"
import { Button } from "../../atoms/Buttons/Button"
import { DashedButton } from "../../atoms/DashedButton"
import { MediaThumbnail } from "../../atoms/MediaThumbnail"
import { Stack } from "../../atoms/containers/Stack"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { Text } from "../../atoms/typography/Text"
import { SAM_TILE_SIZE } from "../../molecules/SamTile"
import { COPY_TEXTS, copyPhotoPress, recordingLabel, type ActivityCopyProps } from "../ActivityCopyBar"
import { recordingPress } from "../ActivityCopyBar/ActivityCopyBar"
import { FLOATING_RESERVE_GAP, useFloatingBase, useFloatingReserve } from "../../templates/PageTemplate/FloatingSlot"

/** Deux pages par ligne dans un volet de 420 : chacune assez grande pour qu'on y reconnaisse sa page. */
const THUMB_SIZE = 180
/** « Ajouter une photo » au bout des pages : une ligne d'action, pas une zone de dépôt. */
const ADD_HEIGHT = 64

const TEXTS = { title: "Mon travail" }

export type ActivityCopyColumnProps = ActivityCopyProps & {
    /** Ce qui flotte au-dessus de « J'ai fini », à droite : la tuile de Sam. Absent : rien. */
    floating?: React.ReactNode
}

export function ActivityCopyColumn(props: ActivityCopyColumnProps) {
    const {
        medium = "photo",
        photos,
        recording,
        instruction,
        onCapture,
        onOpenRecording,
        playback,
        onRerecord,
        onFinish,
        finishing = false,
        finishDisabled = false,
        floating,
        testID,
    } = props

    const audio = medium === "audio"
    const hasWork = audio ? recording != null : photos.length > 0
    // La bulle tandem du live a sa place au pied de la zone ; la tuile de Sam, en fin de défilement.
    const slotRoom = useFloatingReserve()
    const tandemRoom = floating != null ? 0 : slotRoom
    const finishBase = useFloatingBase(hasWork)

    return (
        <View style={styles.root} testID={testID}>
            <View style={[styles.body, tandemRoom > 0 && styles.reserve(tandemRoom)]}>
                <ScrollView
                    contentContainerStyle={styles.scroll(floating != null ? SAM_TILE_SIZE : 0)}
                    showsVerticalScrollIndicator={false}
                >
                    <Stack gap="4">
                        <Stack gap="1">
                            <SectionLabel>{TEXTS.title}</SectionLabel>
                            <Text size="text-sm" color="text-muted">
                                {instruction ?? (audio ? COPY_TEXTS.audioHint : COPY_TEXTS.photoHint)}
                            </Text>
                        </Stack>

                        {!audio && photos.length > 0 && (
                            <View style={styles.grid}>
                                {photos.map((photo, index) => {
                                    const failed = photo.status === "failed"
                                    return (
                                        <MediaThumbnail
                                            key={photo.id}
                                            uri={photo.uri}
                                            size={THUMB_SIZE}
                                            fit="contain"
                                            dimmed={photo.status === "sending"}
                                            failed={failed}
                                            onPress={finishing ? undefined : copyPhotoPress(props, photo, index)}
                                            accessibilityLabel={
                                                failed ? COPY_TEXTS.pageFailed(index + 1) : COPY_TEXTS.page(index + 1)
                                            }
                                        />
                                    )
                                })}
                            </View>
                        )}

                        {audio && recording != null && (
                            <Button
                                variant="outline"
                                {...recordingPress(playback, onOpenRecording, finishing)}
                                label={COPY_TEXTS.recording(formatMinutesSeconds(recording.durationSeconds))}
                                accessibilityLabel={recordingLabel(recording.durationSeconds, playback)}
                                fullWidth
                            />
                        )}
                        {audio && recording != null && onRerecord != null && (
                            <Button
                                variant="ghost"
                                iconName="redo"
                                label={COPY_TEXTS.rerecord}
                                onPress={onRerecord}
                                disabled={finishing}
                                fullWidth
                            />
                        )}

                        {/* Sans rien de rendu, prendre la première page est le seul geste : le bouton
                            de l'univers. Ensuite, ajouter une page se fait au bout des autres. */}
                        {!hasWork && (
                            <Button
                                iconName={audio ? "microphone" : "camera"}
                                label={audio ? COPY_TEXTS.record : COPY_TEXTS.takeFirst}
                                onPress={onCapture}
                                fullWidth
                            />
                        )}
                        {hasWork && !audio && (
                            <DashedButton
                                iconName="camera"
                                label={COPY_TEXTS.takeNext}
                                minHeight={ADD_HEIGHT}
                                onPress={onCapture}
                                disabled={finishing}
                            />
                        )}
                    </Stack>
                </ScrollView>
                {floating != null && (
                    <View style={styles.floating} pointerEvents="box-none">
                        {floating}
                    </View>
                )}
            </View>

            {hasWork && (
                <View style={styles.footer} onLayout={(event) => finishBase(event.nativeEvent.layout.height)}>
                    <Button
                        label={COPY_TEXTS.finish}
                        onPress={onFinish}
                        loading={finishing}
                        disabled={finishDisabled && !finishing}
                        fullWidth
                    />
                </View>
            )}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: {
        flex: 1,
        backgroundColor: theme.colors["surface-raised"],
    },
    body: { flex: 1, minHeight: 0 },
    scroll: (room: number) => ({
        padding: theme.spacing["4"],
        // Ce qui flotte au bas de la zone qui défile : la dernière page remonte au-dessus de lui.
        paddingBottom: room > 0 ? room + theme.spacing["8"] : theme.spacing["4"],
    }),
    /** La place de la bulle tandem, au pied de la zone qui défile : sa hauteur et l'écart au-dessus. */
    reserve: (height: number) => ({ paddingBottom: height + theme.spacing[FLOATING_RESERVE_GAP] }),
    grid: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: theme.spacing["3"],
    },
    floating: {
        position: "absolute",
        left: theme.spacing["4"],
        right: theme.spacing["4"],
        bottom: theme.spacing["3"],
    },
    footer: {
        padding: theme.spacing["4"],
        borderTopWidth: 1,
        borderTopColor: theme.colors.border,
    },
}))
